import { useExportContext } from '@hooks/export/context/ExportContext';
import ExportContextProvider from '@hooks/export/context/ExportContextProvider';
import { enableChartsExporting } from '@ecosystem/export-utils';
import { InsightRendererProps } from '@ecosystem/newron-client-collections';
import { renderLoading } from '@pages/Loading';
import Store from '@state/Store';
import { exportPowerPointImages } from '@utils/collectionUtils/ExportUtils';
import { useFetchChartData } from '@utils/collectionUtils/hooks/UseFetchChartData';
import { FC, useEffect } from 'react';
import { Provider } from 'react-redux';
import PgWarningBanner from '../warning-banner/pg-warning-banner/PgWarningBanner';
import { ProductsLeaderboardDoubleBarChartInsight } from './Model';
import ProductsSegments, { ProductsSegmentsProps } from '@components/products-segments/ProductsSegments';
import { getProductsLeaderboard } from '@services/ProductsLeaderboard.service';
import { getIsUsingCollectionLiveData, getPeriodLabel } from '@utils/collectionUtils/CollectionUtils';
import { getCountryCodes } from '@utils/CountryUtils';
import { getBaseTimePeriodForMATAndYTD, getPeriodicityForMATAndYTD } from '@utils/PeriodUtils';

enableChartsExporting();

type DoubleBarChartInsightRenderer = InsightRendererProps<ProductsLeaderboardDoubleBarChartInsight>;

const DoubleBarChartContainer: FC<DoubleBarChartInsightRenderer> = ({ insight, onLoaded, onExportReady }) => {
  const { apiParams, selectedGrowthType, factRevenue, collectionDataType } = insight.data;
  const { comparisonPeriod, productType, countryId, period, clientId, productGroup, periodType } = apiParams;
  const baseTimePeriod = getBaseTimePeriodForMATAndYTD(period, periodType);
  const { periodicity } = getPeriodicityForMATAndYTD(baseTimePeriod, periodType);
  const { isLoading, chartData } = useFetchChartData({
    apiRequest: async () => {
      const label = await getPeriodLabel({
        clientId,
        periodicity,
        baseTimePeriod,
        comparisonPeriod,
        countryIds: getCountryCodes([{ id: countryId }]),
        productType,
        collectionDataType: getIsUsingCollectionLiveData(period, collectionDataType) ? 'live' : collectionDataType,
        productGroups: [{ label: productGroup[0], value: productGroup[0] }],
      });
      const getPeriodFromLabel = RegExp(/\b\w{3,9} \d{4}(?= vs)/i).exec(label)?.[0] ?? period;
      const isUsingCollectionLiveData = getIsUsingCollectionLiveData(getPeriodFromLabel, insight.dataType);
      const params = {
        ...apiParams,
        productGroup: { label: apiParams?.productGroup[0], value: apiParams?.productGroup[0] },
        isUsingCollectionLiveData,
      };
      const response = await getProductsLeaderboard(params);
      return response.content;
    },
    onLoaded: onLoaded,
  });

  // Export Register
  const { chartRefsIsInitialisedStatus, multipleExportCallbacks } = useExportContext();
  useEffect(() => {
    if (chartRefsIsInitialisedStatus.length === 0 || chartRefsIsInitialisedStatus.some((status) => !status)) {
      return;
    }

    const exportPng = exportPowerPointImages({ exportCallbacks: multipleExportCallbacks });
    onExportReady({ exportPng });
  }, [onExportReady, chartRefsIsInitialisedStatus, multipleExportCallbacks]);

  const props: ProductsSegmentsProps = {
    isLoading: isLoading,
    topSellingProductsContent: chartData ?? [],
    productSpecificationFilters: apiParams.productSpecificationFilters,
    sorting: apiParams.sorting,
    selectedGrowthType: selectedGrowthType,
    factRevenue: factRevenue,
    onExportReady: () => {},
    isUsingCollection: true,
  };

  if (isLoading) {
    return renderLoading();
  }

  return <ProductsSegments {...props} />;
};

const DoubleBarChartInsight: FC<DoubleBarChartInsightRenderer> = (props) => {
  const apiParams = {
    ...props.insight.data.apiParams,
    countries: [{ id: props.insight.data.apiParams.countryId }],
    productGroup: props.insight.data.apiParams.productGroup.map((item) => ({ label: item, value: item })),
  };
  return (
    <Provider store={Store}>
      <ExportContextProvider>
        <PgWarningBanner baseApiParams={apiParams} collectionDataType={props.insight.dataType}>
          <DoubleBarChartContainer {...props} />
        </PgWarningBanner>
      </ExportContextProvider>
    </Provider>
  );
};

export default DoubleBarChartInsight;
